/* eslint-disable prettier/prettier */
import { Group, Stack, Text, TextInput, Select, Loader, TagsInput, Title, Divider } from "@mantine/core";
import { useCallback, useEffect, useMemo } from "react";
import CNPJInput from "@/components/cnpj-input";
import TelInput from "@/components/tel-input";
import { TAM_CNPJ } from "@/utils/constants";
import { useCnpjLookup } from "../hooks/useCnpjLookup";
import { EmpresaStepProperties } from "../types";

export const EmpresaInfoStep = ({ form, isFetchingCnpj }: EmpresaStepProperties) => {
  const { fetchDataByCnpj } = useCnpjLookup(form, "novaEmpresa");

  const porteOptions = useMemo(
    () => [
      { value: "MICRO_EMPREENDEDOR", label: "Microempreendedor Individual" },
      { value: "MICRO_EMPRESA", label: "Microempresa" },
      { value: "EMPRESA_PEQUENO_PORTE", label: "Empresa de Pequeno Porte" },
      { value: "EMPRESA_MEDIO_PORTE", label: "Empresa de Médio Porte" },
      { value: "EMPRESA_GRANDE_PORTE", label: "Empresa de Grande Porte" }
    ],
    []
  );

  const numeroColaboradoresOptions = useMemo(
    () => [
      { value: "ATE_50", label: "Até 50" },
      { value: "DE_51_A_100", label: "De 51 a 100" },
      { value: "DE_101_A_200", label: "De 101 a 200" },
      { value: "DE_201_A_500", label: "De 201 a 500" },
      { value: "ACIMA_DE_500", label: "Acima de 500" }
    ],
    []
  );

  useEffect(() => {
    const cnpjValue = form.values.novaEmpresa.cnpj;
    if (!cnpjValue) return;

    const cnpj = cnpjValue.replaceAll(/\D/g, "");
    if (cnpj.length === TAM_CNPJ) {
      void fetchDataByCnpj(cnpj);
    }
  }, [form.values.novaEmpresa.cnpj, fetchDataByCnpj]);

  const handleSetoresChange = useCallback(
    (value: string[]) => {
      form.setFieldValue("novaEmpresa.setores", value);
    },
    [form]
  );

  return (
    <Stack gap="sm" mt="md">
      <Title order={3} mb="xs">Passo 2: Cadastro da Primeira Empresa</Title>
      <Text size="sm" c="dimmed" mb="md">
        Configure a primeira empresa que será vinculada à licenciada
      </Text>
      <Divider mb="md" />

      <Text size="sm" fw={500} mb="xs">
        Dados Básicos
      </Text>
      <Group>
        <CNPJInput
          label="CNPJ"
          placeholder="00.000.000/0000-00"
          style={{ flex: 1 }}
          rightSection={isFetchingCnpj ? <Loader size="xs" /> : null}
          {...form.getInputProps("novaEmpresa.cnpj")}
          required
        />
        <TextInput
          label="Razão Social"
          style={{ flex: 2 }}
          {...form.getInputProps("novaEmpresa.razaoSocial")}
          required
        />
        <TextInput
          label="Nome Fantasia"
          style={{ flex: 2 }}
          {...form.getInputProps("novaEmpresa.nomeFantasia")}
          required
        />
      </Group>
      <Group grow>
        <TextInput label="Email" type="email" {...form.getInputProps("novaEmpresa.email")} required />
        <TelInput label="Telefone" {...form.getInputProps("novaEmpresa.telefone")} required />
      </Group>
      <Group grow>
        <TextInput label="Código CNAE" {...form.getInputProps("novaEmpresa.codigoCNAE")} required />
        <TextInput label="CNAE" {...form.getInputProps("novaEmpresa.cnae")} required />
      </Group>
      <Group grow>
        <Select
          label="Porte da Empresa"
          placeholder="Selecione o porte"
          data={porteOptions}
          {...form.getInputProps("novaEmpresa.porte")}
          required
        />
        <Select
          label="Número de Colaboradores"
          placeholder="Selecione"
          data={numeroColaboradoresOptions}
          {...form.getInputProps("novaEmpresa.numeroColaboradores")}
          required
        />
      </Group>
      <TagsInput
        label="Setores"
        placeholder="Digite e pressione Enter para adicionar"
        value={Array.isArray(form.values.novaEmpresa.setores) ? form.values.novaEmpresa.setores : []}
        onChange={handleSetoresChange}
        error={form.errors["novaEmpresa.setores"]}
        required
      />

      <Text size="sm" fw={500} mt="md" mb="xs">
        Endereço
      </Text>
      <Group>
        <TextInput
          label="CEP"
          placeholder="00000-000"
          style={{ flex: 1 }}
          {...form.getInputProps("novaEmpresa.endereco.cep")}
          required
        />
        <TextInput
          label="Cidade"
          style={{ flex: 3 }}
          {...form.getInputProps("novaEmpresa.endereco.cidade")}
          required
        />
        <TextInput
          label="Estado"
          placeholder="SP"
          style={{ flex: 0.5, maxWidth: "80px" }}
          {...form.getInputProps("novaEmpresa.endereco.estado")}
          required
        />
      </Group>
      <TextInput label="Logradouro" {...form.getInputProps("novaEmpresa.endereco.logradouro")} required />
      <Group grow>
        <TextInput label="Número" {...form.getInputProps("novaEmpresa.endereco.numero")} required />
        <TextInput label="Complemento" {...form.getInputProps("novaEmpresa.endereco.complemento")} />
        <TextInput label="Bairro" {...form.getInputProps("novaEmpresa.endereco.bairro")} required />
      </Group>

      <Text size="sm" fw={500} mt="md" mb="xs">
        Responsável
      </Text>
      <TextInput label="Nome do Responsável" {...form.getInputProps("novaEmpresa.responsavel.nome")} required />
      <Group grow>
        <TextInput
          label="Email do Responsável"
          type="email"
          {...form.getInputProps("novaEmpresa.responsavel.email")}
          required
        />
        <TelInput
          label="Telefone do Responsável"
          {...form.getInputProps("novaEmpresa.responsavel.telefone")}
          required
        />
      </Group>
    </Stack>
  );
};
